import { plural, type BarFade, type NewsBarDto, type NewsDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { api } from '../lib/api';
import { RichInline, RichText } from './rich-text';

/**
 * Публикация в верхней панели — между меню и колокольчиком. Фон (картинка, GIF или видео)
 * растворяется к выбранным краям; форма и глубина растворения — из настроек публикации.
 */

const SAMPLE = 4;

/**
 * Маска растворения: видимая область — фигура, отступающая от растворяемых краёв на половину
 * глубины, размытая по Гауссу так, что переход занимает всю глубину. Нерастворяемые края
 * вынесены далеко за кадр — размытие до них не дотягивается.
 */
export function fadeMask(w: number, h: number, f: BarFade): string {
  // На узкой полосе (телефон) растворение короче — иначе тексту не останется места.
  const d = Math.min(f.depth, w / (w < 500 ? 5 : 3));
  // Снизу — не больше половины высоты: иначе под текстом не останется фона.
  const db = Math.min(f.depth, h * 0.5);
  const far = 4 * Math.max(d, db) + 40;
  const L = f.left ? d / 2 : -far;
  const R = f.right ? w - d / 2 : w + far;
  const T = -far;
  const B = f.bottom ? h - db / 2 : h + far;
  let shape: string;
  if (f.shape === 'oval') {
    // Овал: без нижнего края — скруглены все углы, с нижним — дуга от верхнего края.
    const cy = f.bottom ? 0 : h / 2;
    const ry = f.bottom ? B : h * 0.9;
    shape = `<ellipse cx="${(L + R) / 2}" cy="${cy}" rx="${(R - L) / 2}" ry="${ry}"/>`;
  } else {
    const k = f.shape === 'diagonal' ? d * 0.7 : 0;
    const amp = f.shape === 'wave' ? d / 4 : 0;
    const ampB = f.shape === 'wave' ? db / 4 : 0;
    const lambda = Math.max(28, h * 0.9);
    // Край не заходит ближе L к рамке: наклон и волна — только внутрь, иначе у рамки остался бы срез.
    const left = (y: number) => (f.left ? L + k * (1 - Math.min(Math.max(y / h, 0), 1)) + amp * (1 + Math.sin((2 * Math.PI * y) / lambda)) : L);
    const right = (y: number) => (f.right ? R - k * (1 - Math.min(Math.max(y / h, 0), 1)) - amp * (1 + Math.sin((2 * Math.PI * y) / lambda + Math.PI)) : R);
    const bottom = (x: number) => (f.bottom ? B - ampB * (1 + Math.sin((2 * Math.PI * x) / 140)) : B);
    const pts: string[] = [];
    const ys = (from: number, to: number) => {
      const out: number[] = [];
      const step = from < to ? SAMPLE : -SAMPLE;
      for (let y = from; from < to ? y < to : y > to; y += step) out.push(y);
      out.push(to);
      return out;
    };
    // Обход: верх (за кадром) → правый край вниз → низ справа налево → левый край вверх.
    for (const y of ys(T, B)) pts.push(`${right(y).toFixed(1)},${y.toFixed(1)}`);
    for (const x of ys(right(B), left(B))) pts.push(`${x.toFixed(1)},${bottom(x).toFixed(1)}`);
    for (const y of ys(B, T)) pts.push(`${left(y).toFixed(1)},${y.toFixed(1)}`);
    shape = `<polygon points="${pts.join(' ')}"/>`;
  }
  const sx = Math.max(d / 4, 0.01);
  const sy = Math.max((f.bottom ? db : Math.min(d, h) / 2) / 4, 0.01);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<filter id="b" filterUnits="userSpaceOnUse" x="${-w}" y="${-h}" width="${3 * w}" height="${3 * h}"><feGaussianBlur stdDeviation="${sx.toFixed(2)} ${sy.toFixed(2)}"/></filter>` +
    `<g filter="url(#b)" fill="#000">${shape}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** «сегодня», «завтра», «через 3 дня»; событие прошло — ничего. */
export function countdown(eventDate: string | null, now = new Date()): string | null {
  if (!eventDate) return null;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = eventDate.split('-').map(Number) as [number, number, number];
  const days = Math.round((Date.UTC(y, m - 1, d) - today) / 86_400_000);
  if (days < 0) return null;
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  return `через ${days} ${plural(days, ['день', 'дня', 'дней'])}`;
}

function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const on = () => setReduce(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduce;
}

export interface BarMedia {
  url: string;
  mime: string;
}

/**
 * Сама полоса. compact — вид телефона (в предпросмотре — независимо от ширины экрана);
 * media — фон из формы (ещё не сохранён), иначе — сохранённый.
 */
export function BarBanner({
  bar,
  compact,
  media,
  onOpen,
  onDismiss,
}: {
  bar: Omit<NewsBarDto, 'from' | 'until' | 'media'> & { media?: NewsBarDto['media'] };
  compact?: boolean;
  media?: BarMedia | null;
  onOpen?: () => void;
  onDismiss?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const reduce = useReducedMotion();
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize((s) => (s.w === Math.round(el.clientWidth) && s.h === Math.round(el.clientHeight) ? s : { w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) }));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const mask = size.w > 0 && size.h > 0 ? fadeMask(size.w, size.h, bar.fade) : undefined;
  const m = media !== undefined ? media : (bar.media ?? null);
  const count = bar.countdown ? countdown(bar.eventDate) : null;
  // Текст начинается за зоной растворения, но не дальше разумного; на телефоне — ближе к краю.
  const inset = (on: boolean, narrow: boolean) => `${on ? Math.round(Math.min(bar.fade.depth, narrow ? 24 : 120) * 0.75) + (narrow ? 8 : 12) : narrow ? 10 : 16}px`;
  const style = {
    '--bar-pos': `50% ${bar.pos}%`,
    '--bar-dim': bar.dim / 100,
    '--bar-pl': inset(bar.fade.left, Boolean(compact)),
    '--bar-pr': inset(bar.fade.right, Boolean(compact)),
    '--bar-pl-narrow': inset(bar.fade.left, true),
    '--bar-pr-narrow': inset(bar.fade.right, true),
  } as CSSProperties;

  return (
    <div
      ref={ref}
      className={`bar${compact ? ' compact' : ''}${onOpen ? ' clickable' : ''}`}
      style={style}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen()) : undefined}
      title={onOpen ? 'Открыть полностью' : undefined}
    >
      <span className="bar-bg" style={mask ? { maskImage: mask, WebkitMaskImage: mask } : { visibility: 'hidden' }} aria-hidden>
        {m ? (
          m.mime.startsWith('video/') ? (
            // Без звука и по кругу; при «уменьшении движения» — первый кадр.
            <video key={m.url} src={m.url} muted loop playsInline autoPlay={!reduce} preload={reduce ? 'metadata' : 'auto'} />
          ) : (
            <img src={m.url} alt="" />
          )
        ) : (
          <span className="bar-plain" />
        )}
        <span className="bar-shade" />
      </span>
      <span className="bar-text">
        <b>{bar.text}</b>
        {bar.caption && <span className="bar-caption">{bar.caption}</span>}
      </span>
      {count && <span className={`bar-count${count === 'сегодня' ? ' today' : ''}`}>{count}</span>}
      {onDismiss && (
        <button
          type="button"
          className="bar-close"
          aria-label="Скрыть у себя"
          title="Скрыть у себя"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

/** Место между меню и колокольчиком: публикация или пустое место. */
export function TopBarSlot() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ['news', 'bar', 'current'],
    queryFn: () => api.get<{ item: NewsDto | null }>('/news/bar/current'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  const item = current.data?.item ?? null;
  const [open, setOpen] = useState(false);
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/news/${id}/dismiss`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['news', 'bar', 'current'] }),
  });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  // Срок показа вышел — панель очищается без перезагрузки страницы.
  useEffect(() => {
    const until = item?.bar?.until ? Date.parse(item.bar.until) - Date.now() : null;
    if (until === null || until > 86_400_000) return;
    const t = setTimeout(() => void qc.invalidateQueries({ queryKey: ['news', 'bar', 'current'] }), Math.max(until, 0) + 1000);
    return () => clearTimeout(t);
  }, [item?.id, item?.bar?.until, qc]);

  if (!item?.bar) return <span className="spacer" />;
  const bar = item.bar;
  return (
    <>
      <BarBanner bar={bar} onOpen={item.body ? () => setOpen(true) : undefined} onDismiss={bar.dismissible ? () => dismiss.mutate(item.id) : undefined} />
      {open && (
        <>
          <div className="drawer-backdrop" onClick={() => setOpen(false)} />
          <div className="dialog bar-dialog" role="dialog" aria-modal="true" aria-labelledby="bar-dialog-title">
            <h2 id="bar-dialog-title">
              <RichInline text={item.title} />
            </h2>
            {bar.eventDate && <span className="faint">{dayFmt.format(new Date(`${bar.eventDate}T12:00:00`))}</span>}
            <div className="news-body">
              <RichText text={item.body} />
            </div>
            <div>
              <button className="btn" onClick={() => setOpen(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
